/**
 * VerificationBadge
 *
 * The public Viveka Verification badge. Renders next to a coach's name
 * on their profile, listing detail pages, and listing cards in search
 * results, signalling to clients that this coach has been credential-
 * verified by Viveka.
 *
 * Visibility rules — Subscription Gating Spec §5.3:
 *
 *   The PUBLIC badge is gated by BOTH:
 *     1. subscriptionDetails.subscription >= 2        (paid Verified tier)
 *     2. verificationStatus === 'approved'            (credential approved)
 *
 *   Just paying for Verified is not enough to display the badge — the
 *   coach must also have completed the verification process (cert
 *   upload + 15-min call + admin approval). A paid-Verified coach in
 *   verificationStatus: 'pending' still gets the INTERNAL Verified UI
 *   (file uploads, reporting, listing cap of 10) but the public badge
 *   stays off until approval.
 *
 * Legacy data note:
 *   The `verificationStatus` field is being introduced by spec §5.3 /
 *   task #96. Users created before that field exists won't have it on
 *   their metadata. We treat MISSING (undefined / null) as a legacy
 *   "grandfathered approved" — i.e. existing paid Verified coaches see
 *   the badge until we backfill the field. New coaches who subscribe
 *   to Verified will get verificationStatus: 'pending' written by the
 *   webhook, and will only see the badge after admin sets 'approved'.
 *
 *   This legacy fallback lets us ship the badge UI before the
 *   verification application flow is built. Remove the fallback (i.e.
 *   require an explicit 'approved' value) once #96 has shipped AND
 *   existing Verified coaches have been backfilled to 'approved'.
 *
 * Props
 *   user       (object)  Sharetribe user / currentUser. The component
 *                        reads
 *                          user.attributes.profile.metadata.subscriptionDetails.subscription
 *                          user.attributes.profile.metadata.verificationStatus
 *                        and renders nothing if either gate fails.
 *
 *   size       (string)  'sm' | 'md' (default). Controls overall scale.
 *
 *   variant    (string)  'pill' (default) — small gold pill with check
 *                        + label, inline alongside a name.
 *                        'icon-only' — just the V-sphere check chip,
 *                        no text. Useful for compact contexts.
 *
 *   showTooltip (boolean) default true. When true, hovering the badge
 *                        surfaces a "Verified by Viveka" title attr.
 *
 * Rendering style: Viveka brand language — gold accent (#FBBF24) on a
 * subtle dark chip, with a small "V" sphere mark. Stays understated so
 * it doesn't clutter listings.
 */

import React from 'react';

const SIZE_TOKENS = {
  sm: {
    fontSize: '11px',
    padding: '2px 8px',
    iconSize: 12,
    gap: '4px',
  },
  md: {
    fontSize: '12px',
    padding: '3px 10px',
    iconSize: 14,
    gap: '5px',
  },
};

const readSubscriptionTier = (user) => {
  const sd =
    user &&
    user.attributes &&
    user.attributes.profile &&
    user.attributes.profile.metadata &&
    user.attributes.profile.metadata.subscriptionDetails;
  if (sd && typeof sd.subscription === 'number') return sd.subscription;
  return 0;
};

const readVerificationStatus = (user) => {
  const status =
    user &&
    user.attributes &&
    user.attributes.profile &&
    user.attributes.profile.metadata &&
    user.attributes.profile.metadata.verificationStatus;
  // null / undefined returns undefined so the caller can apply the
  // legacy-grandfathered fallback explicitly.
  return status;
};

const VerificationBadge = ({
  user,
  size = 'md',
  variant = 'pill',
  showTooltip = true,
}) => {
  const tier = readSubscriptionTier(user);
  if (tier < 2) return null;

  const verificationStatus = readVerificationStatus(user);
  // Legacy fallback: missing field counts as approved for now. See
  // docstring "Legacy data note" — flip to strict once #96 ships AND
  // backfill is complete.
  const isApproved =
    verificationStatus === 'approved' || verificationStatus == null;
  if (!isApproved) return null;

  const tokens = SIZE_TOKENS[size] || SIZE_TOKENS.md;
  const title = showTooltip ? 'Verified by Viveka' : undefined;

  const checkIcon = (
    <svg
      width={tokens.iconSize}
      height={tokens.iconSize}
      viewBox="0 0 16 16"
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      {/* V-sphere disc + check mark */}
      <circle cx="8" cy="8" r="7" fill="#FBBF24" />
      <path
        d="M4.6 8.2l2.1 2.1L11.4 5.6"
        stroke="#0F172A"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );

  if (variant === 'icon-only') {
    return (
      <span
        title={title}
        aria-label="Verified by Viveka"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: tokens.iconSize + 4,
          height: tokens.iconSize + 4,
        }}
      >
        {checkIcon}
      </span>
    );
  }

  // pill (default)
  return (
    <span
      title={title}
      aria-label="Verified by Viveka"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: tokens.gap,
        padding: tokens.padding,
        borderRadius: '999px',
        background: 'rgba(15, 23, 42, 0.08)',
        border: '1px solid rgba(251, 191, 36, 0.55)',
        color: '#0F172A',
        fontSize: tokens.fontSize,
        fontWeight: 700,
        letterSpacing: '0.3px',
        verticalAlign: 'middle',
        lineHeight: 1.2,
      }}
    >
      {checkIcon}
      Verified
    </span>
  );
};

export default VerificationBadge;
